<script setup lang="ts">
/**
 * Optional CV file upload with PDF type + size validation.
 *
 * The native <input type="file"> is visually hidden (sr-only); a styled
 * button matches the rest of the form card (thin gray border, rounded,
 * DM Sans/Roboto), with the selected filename shown next to it. Clicking
 * the button opens the native file picker.
 */
const props = defineProps<{
  name: string
  label: string
  accept?: string
  maxSize?: number
}>()

const file = defineModel<File | null>()
const error = ref('')
const inputRef = ref<HTMLInputElement>()

// MultiStepForm renders twice on the same page (§3 preview + §10 final CTA)
// — a raw `name` as the DOM id would collide across both instances.
const uid = useId()

const validateFile = (f: File | null) => {
  error.value = ''
  if (!f) return
  if (props.accept && !f.type.match(props.accept.replace('*', '.*'))) {
    error.value = 'Nur PDF-Dateien erlaubt'
    file.value = null
    return
  }
  if (props.maxSize && f.size > props.maxSize) {
    error.value = `Maximal ${props.maxSize / 1024 / 1024} MB erlaubt`
    file.value = null
    return
  }
}

const openPicker = () => inputRef.value?.click()

const handleChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  const f = target.files?.[0] || null
  validateFile(f)
  file.value = f
}
</script>

<template>
  <div class="flex flex-col gap-1">
    <label :for="name" class="text-sm font-semibold text-text">{{ label }}</label>
    <div
      class="flex h-12 items-center gap-3 rounded-lg border px-3 text-sm"
      :class="error ? 'border-red-400' : 'border-border'"
    >
      <input
        :id="name"
        :name="name"
        type="file"
        :accept="accept"
        ref="inputRef"
        class="sr-only"
        @change="handleChange"
      />
      <button
        type="button"
        class="shrink-0 cursor-pointer rounded-lg bg-coral-light px-4 py-2 font-semibold text-text active:scale-[0.98] transition-transform"
        @click="openPicker"
      >
        Datei auswählen
      </button>
      <span class="truncate" :class="file ? 'text-text' : 'text-text-muted'">
        {{ file?.name || 'Keine Datei gewählt' }}
      </span>
    </div>
    <p v-if="error" class="text-xs text-red-500">{{ error }}</p>
  </div>
</template>
